import { WS_FALLBACK_INTERVAL, WS_FALLBACK_NO_OF_RETRIES, WS_TIMEOUT } from '@shared/js/websocket/constants';
import { safeSessionStorage } from '@shared/js/safeWebStorage';

import Echo from 'laravel-echo';
import SocketIoClient from 'socket.io-client';

const echoOptions = {
	broadcaster:        'socket.io',
	host:               window.echoHost,
	client:             SocketIoClient,
	rejectUnauthorized: false,
	upgrade:            false,
	transports:         ['websocket'],
	withCredentials:    false,
};

const WS_FAILURE_REPORTED_SESSION_KEY_PREFIX = 'ws-connect-failure-reported';

// Channel + Event Subscription
class Subscription {
	channel = null;

	event = null;

	callback = null;

	echoChannel = null;

	constructor({ channel, event, callback }) {
		this.channel = channel;
		this.event = event;
		if (!callback) throw new Error('Cannot create subscription without a callback.');
		this.callback = (...args) => {
			callback(...args); // eslint-disable-line
		};
	}
}

// WS Client
class WebsocketClient {
	echo = null;
	maxReconnectionDelay = WS_TIMEOUT; // 60 seconds
	subscriptions = [];
	identifier = null;
	enableLogging = true;
	lastConnectError = null;

	constructor(echoInstance) {
		this.echo = echoInstance;
		this.identifier = (Math.random() + 1).toString(36).substring(7);
		this.init();
	}

	log(...args) {
		this.enableLogging && console.log(`[SYNC-${this.identifier}]`, ...args);
	};

	init() {
		this.socket.io.reconnectionDelayMax(WS_TIMEOUT);
		this.socket.io.reconnectionDelay(WS_FALLBACK_INTERVAL);

		this.echo.connector.socket.on('connect', () => {
			this.lastConnectError = null;
		});

		this.echo.connector.socket.on('reconnecting', (attemptNumber) => {
			const currentDelay = this.socket.io.reconnectionDelay();
			this.log('Socket reconnecting:', { attemptNumber, delay: currentDelay });

			// Delay by 5s per attempt, max 60 seconds
			this.socket.io.reconnectionDelay(Math.min(this.maxReconnectionDelay, attemptNumber * WS_FALLBACK_INTERVAL));

			if (attemptNumber >= WS_FALLBACK_NO_OF_RETRIES) {
				this.captureReconnectFailure({ attemptNumber, delay: currentDelay });
			}
		});

		this.echo.connector.socket.on('connect_error', (err) => {
			const errorPayload = this.getConnectErrorPayload(err);
			this.lastConnectError = errorPayload;
			this.log('Connection error.', errorPayload);
		});

		this.echo.connector.socket.on('connect_failed', () => {
			this.log('Connection failed.');
		});

		this.echo.connector.socket.on('disconnect', () => {
			this.log('Socket disconnected.');
		});
	}

	captureReconnectFailure({ attemptNumber, delay }) {
		if (!window.Sentry || this.wasReconnectFailureReportedForHost()) {
			return;
		}

		const host = this.getEchoHost();
		window.Sentry.captureMessage(`Websockets unable to connect after ${WS_FALLBACK_NO_OF_RETRIES} tries to ${host}`, {
			level: 'warning',
			extra: {
				host,
				attemptNumber,
				delay,
				transport:        this.getTransportName(),
				isOnline:         navigator.onLine,
				pageUrl:          window.location.href,
				pagePath:         window.location.pathname,
				userAgent:        navigator.userAgent,
				lastConnectError: this.lastConnectError,
			},
		});

		this.markReconnectFailureReportedForHost();
	}

	getConnectErrorPayload(err) {
		if (!err) {
			return null;
		}

		return {
			message:     err.message,
			name:        err.name,
			code:        err.code,
			type:        err.type,
			description: err.description,
			context:     err.context,
			stack:       err.stack,
		};
	}

	getEchoHost() {
		if (window.echoHost) {
			return window.echoHost;
		}
		if (this.socket && this.socket.io && this.socket.io.uri) {
			return this.socket.io.uri;
		}
		return 'unknown-host';
	}

	getTransportName() {
		if (this.socket && this.socket.io && this.socket.io.engine && this.socket.io.engine.transport) {
			return this.socket.io.engine.transport.name;
		}
		return null;
	}

	getFailureSessionStorageKey() {
		return `${WS_FAILURE_REPORTED_SESSION_KEY_PREFIX}:${this.getEchoHost()}`;
	}

	wasReconnectFailureReportedForHost() {
		return safeSessionStorage.getItem(this.getFailureSessionStorageKey()) === '1';
	}

	markReconnectFailureReportedForHost() {
		safeSessionStorage.setItem(this.getFailureSessionStorageKey(), '1');
	}

	get socket() {
		return this.echo.connector.socket;
	}

	get isConnected() {
		return this.socket.connected;
	}

	get isDisconnected() {
		return this.socket.disconnected;
	}

	subscribe({ channel, event, callback }) {
		this.log(`Subscribe on ${channel}:`, { channel, event, callback });
		const subscription = new Subscription({ channel, event, callback });
		subscription.echoChannel = this.echo.channel(channel).listen(event, callback);
		this.subscriptions.push(subscription);
		return subscription;
	}

	unsubscribe(subscription) {
		this.log(`Unsubscribe on ${subscription.channel}:`, { subscription });
		this.echo.leaveChannel(subscription.channel);
		this.subscriptions = this.subscriptions.filter(sub => sub !== subscription);
	}
}

// create WebsocketClient singleton
(() => {
	if (!window.echoHost) {
		console.warn('Unable to retrieve window.echoHost');
		return;
	}
	if (window.isMocked || window.wsClient) {
		return;
	}
	const echoInstance = !window.isTesting ? new Echo(echoOptions) : window.Echo;
	window.wsClient = new WebsocketClient(echoInstance);
})();

// DEBUG
window.ws = window.wsClient;

export default window.wsClient;
